import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useNewTicket } from '../api/hooks';
import { Button, PageHeader, Section, TextInput } from '../components/ui';
import { getTg } from '../lib/tg';
import { useT } from '../i18n';

export function TicketNew() {
  const navigate = useNavigate();
  const t = useT();
  const create = useNewTicket();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  return (
    <>
      <header style={{ padding: '12px 8px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            color: 'var(--tg-button)',
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={20} strokeWidth={2.4} />
          {t('common.back')}
        </button>
      </header>
      <PageHeader title={t('ticket.new.title')} subtitle={t('ticket.new.subtitle')} />

      <Section>
        <div style={{ padding: 14, display: 'grid', gap: 10 }}>
          <TextInput value={title} onChange={setTitle} placeholder={t('ticket.new.titlePlaceholder')} />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('ticket.new.contentPlaceholder')}
            rows={8}
            style={{
              background: 'var(--tg-secondary-bg)',
              border: 'none',
              borderRadius: 'var(--r-md)',
              padding: '12px 14px',
              fontSize: 15,
              color: 'var(--tg-text)',
              resize: 'vertical',
              minHeight: 140,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <Button
            disabled={title.trim().length === 0 || content.trim().length === 0 || create.isPending}
            loading={create.isPending}
            leading={!create.isPending && <Send size={16} />}
            onClick={() => {
              create.mutate(
                { title: title.trim(), content: content.trim() },
                {
                  onSuccess: (data) => {
                    getTg()?.HapticFeedback.notificationOccurred('success');
                    const id = extractId(data);
                    if (id) navigate(`/tickets/${id}`, { replace: true });
                    else navigate('/tickets', { replace: true });
                  },
                  onError: () => getTg()?.HapticFeedback.notificationOccurred('error'),
                },
              );
            }}
          >
            {t('ticket.new.submit')}
          </Button>
        </div>
      </Section>
    </>
  );
}

function extractId(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  const inner = (obj.data as Record<string, unknown> | undefined) ?? obj;
  const id = inner.id ?? inner.uuid;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
}
